import { rtkApi } from '../rtkApi';

export interface WireInstructions {
  beneficiary: string;
  bankName: string;
  routingNumber: string;
  accountNumber: string;
  reference: string;
  publishedAt?: string;
}

export interface EarnestDeposit {
  key: string;
  label: string;
  amount: number;
  dueDate?: string;
  status: 'pending' | 'submitted' | 'confirmed';
  confirmedAt?: string;
  confirmedByRole?: string;
  note?: string;
}

export interface ClosingOrchestration {
  contractId: string;
  isCash: boolean;
  isCommercial: boolean;
  titleCompany: string;
  wireInstructions: WireInstructions | null;
  wireVerifiedAt?: string;
  closingDisclosureAcknowledgedAt?: string;
  closingAppointment?: {
    date?: string;
    time?: string;
    location?: string;
    titleContactPhone?: string;
  } | null;
  earnestDeposits: EarnestDeposit[];
  closedAt?: string;
  closedByRole?: string;
  contractStatus?: string;
}

export const closingApi = rtkApi.injectEndpoints({
  endpoints: (builder) => ({
    getClosingOrchestration: builder.query<ClosingOrchestration, string>({
      query: (contractId) => ({
        url: `/closing/contract/${contractId}`,
        method: 'GET',
      }),
      transformResponse: (response: {
        success: boolean;
        data: ClosingOrchestration;
      }) => response.data,
      providesTags: (_r, _e, contractId) => [
        { type: 'ClosingOrchestration', id: contractId },
      ],
    }),

    updateClosingOrchestration: builder.mutation<
      ClosingOrchestration,
      {
        contractId: string;
        wireInstructions?: Partial<WireInstructions>;
        closingAppointment?: ClosingOrchestration['closingAppointment'];
      }
    >({
      query: ({ contractId, ...body }) => ({
        url: `/closing/contract/${contractId}`,
        method: 'PATCH',
        data: body,
      }),
      transformResponse: (response: {
        success: boolean;
        data: ClosingOrchestration;
      }) => response.data,
      invalidatesTags: (_r, _e, { contractId }) => [
        { type: 'ClosingOrchestration', id: contractId },
        'ContractPipeline',
        'ContractActivity',
      ],
    }),

    verifyWireInstructions: builder.mutation<ClosingOrchestration, string>({
      query: (contractId) => ({
        url: `/closing/contract/${contractId}/verify-wire`,
        method: 'POST',
      }),
      transformResponse: (response: {
        success: boolean;
        data: ClosingOrchestration;
      }) => response.data,
      invalidatesTags: (_r, _e, contractId) => [
        { type: 'ClosingOrchestration', id: contractId },
        'ContractPipeline',
        'ContractActivity',
      ],
    }),

    acknowledgeClosingDisclosure: builder.mutation<
      ClosingOrchestration,
      string
    >({
      query: (contractId) => ({
        url: `/closing/contract/${contractId}/acknowledge-cd`,
        method: 'POST',
      }),
      transformResponse: (response: {
        success: boolean;
        data: ClosingOrchestration;
      }) => response.data,
      invalidatesTags: (_r, _e, contractId) => [
        { type: 'ClosingOrchestration', id: contractId },
        'ContractPipeline',
      ],
    }),

    updateEarnestDeposit: builder.mutation<
      ClosingOrchestration,
      {
        contractId: string;
        key: string;
        status: EarnestDeposit['status'];
        note?: string;
      }
    >({
      query: ({ contractId, ...body }) => ({
        url: `/closing/contract/${contractId}/earnest`,
        method: 'POST',
        data: body,
      }),
      transformResponse: (response: {
        success: boolean;
        data: ClosingOrchestration;
      }) => response.data,
      invalidatesTags: (_r, _e, { contractId }) => [
        { type: 'ClosingOrchestration', id: contractId },
        'ContractActivity',
      ],
    }),

    markContractClosed: builder.mutation<ClosingOrchestration, string>({
      query: (contractId) => ({
        url: `/closing/contract/${contractId}/mark-closed`,
        method: 'POST',
      }),
      transformResponse: (response: {
        success: boolean;
        data: ClosingOrchestration;
      }) => response.data,
      invalidatesTags: (_r, _e, contractId) => [
        { type: 'ClosingOrchestration', id: contractId },
        'Contract',
        'ContractPipeline',
        'ContractActivity',
      ],
    }),
  }),
});

export const {
  useGetClosingOrchestrationQuery,
  useUpdateClosingOrchestrationMutation,
  useVerifyWireInstructionsMutation,
  useAcknowledgeClosingDisclosureMutation,
  useUpdateEarnestDepositMutation,
  useMarkContractClosedMutation,
} = closingApi;
